// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { DataTableBody, DataTableHead, DataTableRow } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomerPartNumberItem from "@steiner/App/Components/CustomerPartNumbers/CustomerPartNumberItem";
import CustomerPartNumberItemWithoutProduct from "@steiner/App/Components/CustomerPartNumbers/CustomerPartNumberItemWithoutProduct";
import HomeAccordion from "@steiner/App/Components/Home/HomeAccordion";
import Notify from "@steiner/App/Components/Notify";
import loadCustomerPartNumbers from "@steiner/App/Handlers/CustomerPartNumber/LoadCustomerPartNumbers";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import { getSettingsCollection } from "@steiner/App/Store/Selectors/SettingsSelectors";
import DataTable, { DataTablePresentationProps } from "@steiner/MobiusOverrides/DataTable";
import DataTableHeader from "@steiner/MobiusOverrides/DataTable/DataTableHeader";
import { XNGAGE_ELEMENTS } from "@steiner/XDL/constants";
import qs from "qs";
import React, { useEffect, useState } from "react";
import { FC } from "react";
import { connect, ResolveThunks } from "react-redux";

const labels = {
    productDescription: translate("Product Description"),
    yourPartNumber: translate("Your Part #"),
    edit: translate("Edit"),
    delete: translate("Delete"),
    addToCart: translate("Add to Cart"),
    addToList: translate("Add to List"),
};

enum fields {
    showAddToCart = "showAddToCart",
    showAddToList = "showAddToList",
}
export interface OwnProps extends WidgetProps {
    fields: {
        [fields.showAddToCart]: boolean;
        [fields.showAddToList]: boolean;
    };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => {
    return {
        customerProducts: state.xCustomerPartNumbers.customerProducts,
        isLoading: state.xCustomerPartNumbers.isLoading,
        pagination: state.xCustomerPartNumbers.pagination,
        customerNumber: getBillToState(state, getSession(state).billToId).value?.customerNumber,
        customerPartNumberPageUrl: getPageLinkByPageType(state, "CustomerPartNumberPage")?.url,
        products: getProductsForProductInfoList(state, ownProps.id),
        exportCustomerPartNumbersUrl: getSettingsCollection(state).customSharedSettings.exportCustomerPartNumbers,
    };
};

const mapDispatchToProps = {
    loadCustomerPartNumbers,
    loadProductInfoList,
};

type Props = WidgetProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

export interface CustomerPartNumbersStyles {
    downloadLinksWrapper?: InjectableCss;
    csvFileLink?: LinkPresentationProps;
    textFileLink?: LinkPresentationProps;
    notFoundMessage?: TypographyProps;
    notFoundContainer?: InjectableCss;
    tableStyles?: DataTablePresentationProps;
}

export const customerPartNumbersStyles: CustomerPartNumbersStyles = {};

const styles = customerPartNumbersStyles;
export const RecentCustomerPartNumbers: FC<Props> = ({
    customerProducts,
    isLoading,
    pagination,
    customerNumber,
    customerPartNumberPageUrl,
    id: widgetId,
    products,
    fields,
    loadProductInfoList,
    loadCustomerPartNumbers,
    exportCustomerPartNumbersUrl,
}) => {
    useEffect(() => {
        loadCustomerPartNumbers({
            pageSize: 16,
        });
    }, []);

    useEffect(() => {
        if (!customerProducts || customerProducts.length === 0) {
            return;
        }
        const productErps = customerProducts
            .filter(product => !!product.productErpNumber)
            .map(product => product.productErpNumber!);

        loadProductInfoList({
            id: widgetId,
            forceLoad: true,
            getProductCollectionParameter: {
                productNumbers: productErps,
            },
        });
    }, [customerProducts]);

    const getFileDownloadLink = (type: string) => {
        const queryString = qs.stringify({ fileType: type, customerNumber });
        return `${exportCustomerPartNumbersUrl}${queryString !== "" ? `?${queryString}` : ""}`;
    };

    if (!customerProducts || customerProducts.length === 0) {
        return null;
    }

    return (
        <>
            <HomeAccordion
                title={translate("Edit Customer Part #")}
                pagination={pagination}
                itemsCount={customerProducts?.length || 0}
                viewAll={customerPartNumberPageUrl}
                isLoading={isLoading}
                showViewAll={true}
                showPagination={true}
            >
                <StyledWrapper {...styles.downloadLinksWrapper}>
                    <Link {...styles.csvFileLink} href={getFileDownloadLink("csv")} target="_blank">
                        {translate("CSV")}
                    </Link>
                    <Link {...styles.textFileLink} href={getFileDownloadLink("txt")} target="_blank">
                        {translate("Text")}
                    </Link>
                </StyledWrapper>
                {!customerProducts || customerProducts.length === 0 ? (
                    <>
                        {!isLoading && (
                            <StyledWrapper {...styles.notFoundContainer}>
                                <Notify {...styles.notFoundMessage}>{translate("No customer products found.")}</Notify>
                            </StyledWrapper>
                        )}
                    </>
                ) : (
                    <DataTable {...styles.tableStyles}>
                        <DataTableHead>
                            <DataTableHeader>{labels.productDescription}</DataTableHeader>
                            <DataTableHeader>{labels.yourPartNumber}</DataTableHeader>
                            <DataTableHeader>{labels.edit}</DataTableHeader>
                            {fields.showAddToCart && <DataTableHeader>{labels.addToCart}</DataTableHeader>}
                            {fields.showAddToList && <DataTableHeader>{labels.addToList}</DataTableHeader>}
                        </DataTableHead>
                        <DataTableBody>
                            {customerProducts.map(customerProduct => {
                                const product = products?.find(
                                    product => product.productNumber === customerProduct.productErpNumber,
                                );
                                if (product) {
                                    return (
                                        <CustomerPartNumberItem
                                            key={customerProduct.productErpNumber}
                                            customerProduct={customerProduct}
                                            product={product}
                                            labels={labels}
                                            showAddToCart={fields.showAddToCart}
                                            showAddToList={fields.showAddToList}
                                            widgetId={widgetId}
                                        />
                                    );
                                }
                                return (
                                    <CustomerPartNumberItemWithoutProduct
                                        key={customerProduct.productErpNumber}
                                        customerProduct={customerProduct}
                                        labels={labels}
                                        showAddToCart={fields.showAddToCart}
                                        showAddToList={fields.showAddToList}
                                    />
                                );
                            })}
                        </DataTableBody>
                    </DataTable>
                )}
            </HomeAccordion>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withToaster(RecentCustomerPartNumbers)),
    definition: {
        group: XNGAGE_ELEMENTS,
        allowedContexts: ["HomePage", "MyAccountPage"],
        fieldDefinitions: [
            {
                name: fields.showAddToCart,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showAddToList,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
